import React from "react";
import { App } from "../types/app";

interface ProductCardProps {
  app: App;
}

const ProductCard: React.FC<ProductCardProps> = ({ app }) => {
  const openURL = (url: string) => {
    window.open(url, "_blank");
  };

  const platforms = [
    { key: "ios", label: "iOS", url: app.url.ios },
    { key: "android", label: "Android", url: app.url.android },
    { key: "macos", label: "macOS", url: app.url.macos },
  ].filter((p) => p.url);

  return (
    <div className="group bg-white rounded-2xl border border-brand-border p-6 sm:p-8
                    hover:shadow-lg hover:-translate-y-1 transition-all duration-300">
      {/* App Icon */}
      <div className="flex justify-center mb-5">
        <img
          src={app.icon}
          alt={app.name}
          className="w-20 h-20 rounded-2xl object-cover shadow-sm"
        />
      </div>

      {/* App Info */}
      <div className="text-center mb-5">
        <h3 className="text-lg font-semibold text-brand-text mb-2">
          {app.name}
        </h3>
        <p className="text-sm text-brand-text-secondary leading-relaxed">
          {app.desc}
        </p>
      </div>

      {/* Divider */}
      <div className="border-t border-brand-border mb-5" />

      {/* Download Links */}
      <div className="flex flex-wrap justify-center gap-2">
        {platforms.map((platform) => (
          <button
            key={platform.key}
            onClick={() => openURL(platform.url!)}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm text-brand-text-secondary
                       bg-brand-bg-alt rounded-full hover:bg-gray-200 hover:text-brand-text
                       transition-colors duration-200"
          >
            <PlatformIcon platform={platform.key} />
            {platform.label}
          </button>
        ))}
      </div>
    </div>
  );
};

const PlatformIcon: React.FC<{ platform: string }> = ({ platform }) => {
  switch (platform) {
    case "ios":
      return (
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
          <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
        </svg>
      );
    case "android":
      return (
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
          <path d="M17.523 15.341c-.76 0-1.378.616-1.378 1.378s.618 1.378 1.378 1.378 1.378-.616 1.378-1.378-.618-1.378-1.378-1.378zm-11.046 0c-.76 0-1.378.616-1.378 1.378s.618 1.378 1.378 1.378 1.378-.616 1.378-1.378-.618-1.378-1.378-1.378zm11.42-5.07l2.26-3.931c.128-.223.045-.506-.178-.634-.223-.128-.506-.045-.634.178l-2.307 4.011c-1.387-.627-2.946-.987-4.608-.987s-3.221.36-4.608.987L5.122 5.884c-.128-.223-.411-.306-.634-.178-.223.128-.306.411-.178.634l2.26 3.931C4.017 11.873 2.5 14.078 2.5 16.5h19c0-2.422-1.517-4.627-4.103-6.229z" />
        </svg>
      );
    case "macos":
      return (
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
          <path d="M22 17.607c-.786 2.28-3.139 6.317-5.563 6.361-1.608.031-2.125-.953-3.963-.953-1.837 0-2.412.923-3.932.983-2.572.099-6.542-5.825-6.542-10.991 0-4.747 3.037-7.1 6.037-7.117 1.513-.006 2.93 1.027 3.871 1.027.94 0 2.649-1.27 4.471-1.087.761.031 2.888.305 4.257 2.311-3.743 2.442-3.158 7.549.357 9.361zM15.563 4.06c.742-.892 1.249-2.142 1.108-3.397-1.214.08-2.655.81-3.483 1.702-.738.792-1.378 2.06-1.206 3.287 1.339.046 2.687-.694 3.581-1.592z" />
        </svg>
      );
    default:
      return null;
  }
};

export default ProductCard;
